import React from 'react';
import { Package, MapPin, LucideIcon } from 'lucide-react';
import { CourierTotals } from '../../../../core/graphql/types';

interface CourierTotalsSectionProps {
  totalCourierCharges: number;
  courierTotals: CourierTotals | null | undefined;
}

interface TotalCard {
  title: string;
  value: number;
  icon: LucideIcon | (() => JSX.Element);
  description: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
}

const CourierTotalsSection = ({
  totalCourierCharges,
  courierTotals,
}: CourierTotalsSectionProps) => {
  const totalCards: TotalCard[] = [
    {
      title: 'Total Courier Charges',
      value: totalCourierCharges,
      icon: Package,
      description: 'Parcels loaded by the courier from agency',
      bgColor: 'bg-light-mode-green dark:bg-dark-mode-green',
      borderColor: 'border-green dark:border-bright-green',
      iconColor: 'text-bright-green',
    },
    {
      title: 'Total Delivery Deliveries',
      value: courierTotals?.totalDeliveries ?? 0,
      icon: MapPin,
      description: 'Parcels delivered into OOH points',
      bgColor: 'bg-light-mode-green dark:bg-dark-mode-green',
      borderColor: 'border-green dark:border-bright-green',
      iconColor: 'text-bright-green',
    },
    {
      title: 'Total C2C Discharges',
      value: courierTotals?.totalC2CDischarges ?? 0,
      icon: () => (
        <div className="flex h-5 w-6 items-center justify-center rounded-full bg-bright-blue">
          <div className="h-1 w-1 rounded-full bg-white"></div>
        </div>
      ),
      description: 'C2C parcels discharged from OOH points',
      bgColor: 'dark:bg-night-dark-blue',
      borderColor: 'border-bright-blue',
      iconColor: '',
    },
    {
      title: 'Total FM Discharges',
      value: courierTotals?.totalFMDischarges ?? 0,
      icon: () => (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500">
          <div className="h-1 w-1 rounded-full bg-white"></div>
        </div>
      ),
      description: 'FM parcels discharged from OOH points',
      bgColor: 'bg-orange-200/80 dark:bg-orange-400/80',
      borderColor: 'border-orange-500',
      iconColor: '',
    },
    {
      title: 'Total Expired',
      value: courierTotals?.totalExpired ?? 0,
      icon: () => (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-800">
          <div className="h-1 w-1 rounded-full bg-white"></div>
        </div>
      ),
      description: 'Expired parcels discharged from OOH points',
      bgColor: 'bg-rose-500 dark:bg-red/90',
      borderColor: 'border-rose-600 dark:border-red',
      iconColor: '',
    },
    {
      title: 'Total Returns',
      value: courierTotals?.totalReturns ?? 0,
      icon: () => (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-900">
          <div className="h-1 w-1 rounded-full bg-white"></div>
        </div>
      ),
      description: 'Returned parcels discharged from OOH points',
      bgColor: 'bg-purple-400 dark:bg-purple-500',
      borderColor: 'border-red-200 dark:border-purple-900',
      iconColor: '',
    },
  ];

  return (
    <div className="border-t border-gray pt-6">
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3 lg:grid-rows-2 ">
        {totalCards.map((card, index) => {
          const IconComponent = card.icon;

          return (
            <div
              key={index}
              className={`rounded-sm border ${card.borderColor} ${card.bgColor} p-4`}
            >
              <div className="flex items-center justify-center gap-2 lg:justify-between">
                <div className="flex items-center gap-2">
                  {typeof IconComponent === 'function' &&
                  IconComponent.length === 0 ? (
                    <IconComponent />
                  ) : (
                    React.createElement(IconComponent as LucideIcon, {
                      className: `h-5 w-5 ${card.iconColor}`,
                    })
                  )}
                  <span className="text-left text-sm font-medium">
                    {card.title}
                  </span>
                </div>
                <div className="text-center text-2xl font-bold">
                  {card.value}
                </div>
              </div>
              <p className="mt-2 text-center text-xs lg:text-left">
                {card.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CourierTotalsSection;
